import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  useTranslation,
  mapStoreError,
  RESERVED_SLUGS,
  useAuthSession,
  signOut,
  createStoreDetailsSchema,
  getStorefrontBaseDomainFromOrigin,
} from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import type { TranslationKey, StoreErrorKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ImageUpload } from '@/components/forms/image-upload';
import { useCreateStore } from '@/hooks/use-create-store';
import { useCheckSlug } from '@/hooks/use-check-slug';
import { useSlugSuggestion } from '@/hooks/use-slug-suggestion';
import { supabase } from '@/lib/supabase';

// --- Zod Schema ---

// Exported for unit tests to enumerate error translation keys.
// eslint-disable-next-line react-refresh/only-export-components
export const createStoreSchema = createStoreDetailsSchema.extend({
  slug: z
    .string()
    .min(3, { message: 'admin.createStore.errors.slugTooShort' })
    .max(60, { message: 'admin.createStore.errors.slugTooLong' })
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, {
      message: 'admin.createStore.errors.slugInvalidFormat',
    })
    .refine((val) => !RESERVED_SLUGS.has(val), {
      message: 'admin.createStore.errors.slugReserved',
    }),
});

export type CreateStoreFormValues = z.infer<typeof createStoreSchema>;

export function CreateStoreRoute() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  // Rev 2: need session.user.id to pass as ownerId to createStore
  const { user } = useAuthSession(supabase);
  const queryClient = useQueryClient();

  const [submitError, setSubmitError] = useState<StoreErrorKey | null>(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [debouncedSlug, setDebouncedSlug] = useState('');
  const storefrontBaseDomain = getStorefrontBaseDomainFromOrigin(env.VITE_PUBLIC_STOREFRONT_URL);

  const createStoreMutation = useCreateStore();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateStoreFormValues>({
    resolver: zodResolver(createStoreSchema),
    mode: 'onBlur',
    defaultValues: { name: '', description: '', slug: '' },
  });

  const nameValue = watch('name');
  const slugValue = watch('slug');

  // Auto-suggest slug from name (unless user manually edited the slug)
  const suggestedSlug = useSlugSuggestion(nameValue);
  useEffect(() => {
    if (!slugManuallyEdited && suggestedSlug) {
      setValue('slug', suggestedSlug, { shouldValidate: false });
    }
  }, [suggestedSlug, slugManuallyEdited, setValue]);

  // Debounce slug for uniqueness check (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSlug(slugValue), 300);
    return () => clearTimeout(timer);
  }, [slugValue]);

  const { isAvailable, isChecking } = useCheckSlug(debouncedSlug);

  // Track manual slug edits
  const handleSlugChange = useCallback(() => {
    setSlugManuallyEdited(true);
  }, []);

  const handleSignOut = useCallback(() => {
    void signOut(supabase).finally(() => {
      window.location.assign('/auth/signin');
    });
  }, []);

  const onSubmit = async (values: CreateStoreFormValues) => {
    setSubmitError(null);

    // Extra guard: if slug check says taken, don't submit
    if (isAvailable === false) {
      setSubmitError('admin.createStore.errors.slugTaken');
      return;
    }

    // Rev 2: AuthGuard guarantees user is authenticated, but guard for safety
    if (!user) return;

    try {
      const result = await createStoreMutation.mutateAsync({
        input: {
          name: values.name,
          description: values.description,
          slug: values.slug,
          ownerId: user.id, // Rev 2: owner_id is NOT NULL, RLS checks but doesn't set
        },
        logo,
      });
      const accessQueryKey = ['admin', 'access', user.id] as const;
      queryClient.setQueryData(accessQueryKey, {
        state: 'member',
        storeId: result.store.id,
        storeSlug: result.store.slug,
        merchantStatus: null,
      });
      navigate('/dashboard', {
        replace: true,
        state: {
          justCreatedStore: true,
          // Rev 2: pass logoFailed flag so dashboard can show a warning toast
          logoFailed: result.logoFailed,
        },
      });
      // Refresh the seeded access cache after navigation so AuthGuard sees the
      // new membership immediately and then reconciles with the database.
      void queryClient.invalidateQueries({ queryKey: accessQueryKey });
    } catch (err) {
      console.error(err);
      const key = mapStoreError(err);
      setSubmitError(key);
    }
  };

  return (
    <main
      dir={dir}
      data-testid="create-store-route"
      className="bg-background mx-auto min-h-screen max-w-screen-md px-4 py-10 sm:px-6 sm:py-12"
    >
      <section
        className="bg-card border-border rounded-xl border p-6 shadow-sm sm:p-8"
        data-testid="create-store-surface"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="font-display text-foreground text-start text-3xl sm:text-4xl">
              {t('admin.createStore.title')}
            </h1>
            <p className="text-muted-foreground mt-2 text-start">
              {t('admin.createStore.subtitle')}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0 self-start"
            onClick={handleSignOut}
            data-testid="create-store-sign-out"
          >
            {t('admin.authBoundary.signOut')}
          </Button>
        </div>

        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="mt-8 space-y-6"
          data-testid="create-store-form"
        >
          {/* Store name */}
          <div>
            <Label htmlFor="store-name" className="text-foreground">
              {t('admin.createStore.nameLabel')}
            </Label>
            <Input
              id="store-name"
              type="text"
              autoComplete="organization"
              placeholder={t('admin.createStore.namePlaceholder')}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'store-name-error' : undefined}
              data-testid="store-name"
              {...register('name')}
            />
            {errors.name ? (
              <p
                id="store-name-error"
                role="alert"
                className="text-destructive mt-1 text-start text-sm"
                data-testid="store-name-error"
              >
                {t(errors.name.message as TranslationKey)}
              </p>
            ) : null}
          </div>

          {/* Store description */}
          <div>
            <Label htmlFor="store-description" className="text-foreground">
              {t('admin.createStore.descriptionLabel')}
            </Label>
            <Textarea
              id="store-description"
              rows={3}
              maxLength={500}
              placeholder={t('admin.createStore.descriptionPlaceholder')}
              aria-invalid={!!errors.description}
              aria-describedby={errors.description ? 'store-description-error' : undefined}
              data-testid="store-description"
              {...register('description')}
            />
            <div className="mt-1 flex items-center justify-between">
              {errors.description ? (
                <p
                  id="store-description-error"
                  role="alert"
                  className="text-destructive text-start text-sm"
                  data-testid="store-description-error"
                >
                  {t(errors.description.message as TranslationKey)}
                </p>
              ) : (
                <span />
              )}
              <span className="text-muted-foreground text-xs" data-testid="store-description-count">
                {watch('description').length}/500
              </span>
            </div>
          </div>

          {/* Store slug */}
          <div>
            <Label htmlFor="store-slug" className="text-foreground">
              {t('admin.createStore.slugLabel')}
            </Label>
            <Input
              id="store-slug"
              type="text"
              dir="ltr"
              autoComplete="off"
              placeholder={t('admin.createStore.slugPlaceholder')}
              aria-invalid={!!errors.slug || isAvailable === false}
              aria-describedby={
                errors.slug
                  ? 'store-slug-error'
                  : isAvailable === false
                    ? 'store-slug-taken'
                    : 'store-slug-hint'
              }
              data-testid="store-slug"
              {...register('slug', {
                onChange: handleSlugChange,
              })}
            />
            {/* Slug status indicators */}
            {errors.slug ? (
              <p
                id="store-slug-error"
                role="alert"
                className="text-destructive mt-1 text-start text-sm"
                data-testid="store-slug-error"
              >
                {t(errors.slug.message as TranslationKey)}
              </p>
            ) : isChecking ? (
              <p
                className="text-muted-foreground mt-1 text-start text-sm"
                data-testid="store-slug-checking"
              >
                {t('admin.createStore.slugChecking')}
              </p>
            ) : isAvailable === false ? (
              <p
                id="store-slug-taken"
                role="alert"
                className="text-destructive mt-1 text-start text-sm"
                data-testid="store-slug-taken"
              >
                {t('admin.createStore.errors.slugTaken')}
              </p>
            ) : isAvailable === true ? (
              <p
                className="text-admin-lime-hover mt-1 text-start text-sm"
                data-testid="store-slug-available"
              >
                {t('admin.createStore.slugAvailable')}
              </p>
            ) : null}
            <p
              id="store-slug-hint"
              className="text-muted-foreground mt-1 text-start text-sm"
              data-testid="store-slug-hint"
            >
              {t('admin.createStore.slugHint', {
                domain: storefrontBaseDomain,
                slug: slugValue || 'my-store',
              })}
            </p>
          </div>

          {/* Logo upload */}
          <div>
            <Label className="text-foreground">{t('admin.createStore.logoLabel')}</Label>
            <p className="text-muted-foreground mb-2 text-start text-sm">
              {t('admin.createStore.logoHint')}
            </p>
            <ImageUpload value={logo} onChange={setLogo} testIdPrefix="store-logo" />
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={createStoreMutation.isPending}
            aria-disabled={createStoreMutation.isPending}
            className="w-full"
            data-testid="create-store-submit"
          >
            {createStoreMutation.isPending ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden />
                {t('admin.createStore.submitting')}
              </>
            ) : (
              t('admin.createStore.submit')
            )}
          </Button>

          {/* Submit-level error */}
          {submitError ? (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/10 text-destructive rounded-lg border p-3 text-start text-sm"
              data-testid="create-store-submit-error"
            >
              {t(submitError as TranslationKey)}
            </div>
          ) : null}
        </form>
      </section>
    </main>
  );
}
